/**
 * Travel Module - Utilities
 * 旅行模块的工具函数
 */

import {
  CreateTravelFormData,
  GeneratedItinerary,
  ItineraryDay,
  ItineraryItem,
} from './types';
import {
  MAX_PROJECT_NAME_LENGTH,
  MAX_PROJECT_DESCRIPTION_LENGTH,
  MIN_BUDGET,
  MAX_BUDGET,
  DEFAULT_MIN_PARTICIPANTS,
  DEFAULT_MAX_PARTICIPANTS,
} from './constants';

/**
 * 表单验证错误类型
 */
export interface ValidationError {
  field: string;
  message: string;
}

/**
 * 验证旅行创建表单
 * @param formData 表单数据
 * @param t 翻译函数
 * @returns 验证错误数组，如果为空则表示验证通过
 */
export function validateCreateTravelForm(
  formData: CreateTravelFormData,
  t: (key: string) => string
): ValidationError[] {
  const errors: ValidationError[] = [];

  // 验证项目名称
  if (!formData.projectName || formData.projectName.trim().length === 0) {
    errors.push({
      field: 'projectName',
      message: t('createTravel.validation.projectNameRequired'),
    });
  } else if (formData.projectName.length > MAX_PROJECT_NAME_LENGTH) {
    errors.push({
      field: 'projectName',
      message: t('createTravel.validation.projectNameTooLong'),
    });
  }

  // 验证目的地
  if (!formData.selectedCountry || formData.selectedCountry.trim().length === 0) {
    errors.push({
      field: 'selectedCountry',
      message: t('createTravel.validation.destinationRequired'),
    });
  }

  if (!formData.selectedCity) {
    errors.push({
      field: 'selectedCity',
      message: t('createTravel.validation.cityRequired'),
    });
  }

  // 验证日期
  // 允许同一天出发和结束（即 startDate === endDate 为有效），仅当 startDate 晚于 endDate 时视为无效
  if (formData.startDate > formData.endDate) {
    errors.push({
      field: 'dates',
      message: t('createTravel.validation.invalidDateRange'),
    });
  }

  // 验证参与人数
  const minPart = parseInt(formData.minParticipants, 10);
  const maxPart = parseInt(formData.maxParticipants, 10);

  if (isNaN(minPart) || minPart < 1) {
    errors.push({
      field: 'minParticipants',
      message: t('createTravel.validation.invalidMinParticipants'),
    });
  }

  if (isNaN(maxPart) || maxPart < minPart) {
    errors.push({
      field: 'maxParticipants',
      message: t('createTravel.validation.invalidMaxParticipants'),
    });
  }

  // 验证预算（如果提供）
  if (formData.budgetPerPerson) {
    const budget = parseFloat(formData.budgetPerPerson);
    if (isNaN(budget) || budget < MIN_BUDGET || budget > MAX_BUDGET) {
      errors.push({
        field: 'budgetPerPerson',
        message: t('createTravel.validation.invalidBudget'),
      });
    }
  }

  // 验证描述长度（如果提供）
  if (formData.projectDescription && formData.projectDescription.length > MAX_PROJECT_DESCRIPTION_LENGTH) {
    errors.push({
      field: 'projectDescription',
      message: t('createTravel.validation.descriptionTooLong'),
    });
  }

  return errors;
}

/**
 * 安全的 JSON 序列化器，避免循环引用错误
 * @param obj 要序列化的对象
 * @param fallback 失败时的默认返回值
 * @returns JSON字符串
 */
export function safeStringify(obj: any, fallback = ''): string {
  try {
    const seen = new WeakSet();
    return JSON.stringify(
      obj,
      function (_key, value) {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) return '[Circular]';
          seen.add(value);
        }
        if (typeof value === 'function') return value.toString();
        return value;
      },
      2
    );
  } catch (e) {
    try {
      if (obj instanceof Error) {
        return JSON.stringify(
          { message: obj.message, stack: (obj as any).stack },
          null,
          2
        );
      }
    } catch (e2) {
      // ignore
    }
    try {
      return String(obj);
    } catch (e3) {
      return fallback;
    }
  }
}

/**
 * 清洗字符串，移除代码块标记和多余的空白
 * @param s 要清洗的字符串或对象
 * @returns 清洗后的字符串
 */
export function stripCodeBlocks(s: any): string {
  if (s === null || s === undefined) return '';
  if (typeof s !== 'string') return String(s);

  // 移除代码块标记和多余的空白
  let t = s.replace(/```(?:json)?\s*/gi, '').replace(/```/g, '').trim();

  // 如果看起来像JSON，尝试解析并提取name/note
  if (t.startsWith('{') || t.startsWith('[')) {
    try {
      const parsed = JSON.parse(t);
      if (typeof parsed === 'object') {
        if (parsed.name) return String(parsed.name);
        if (parsed.note) return String(parsed.note).slice(0, 200);
        return JSON.stringify(parsed).slice(0, 200);
      }
    } catch (e) {
      // 不是JSON，保持原样
    }
  }

  return t;
}

/**
 * 清洗行程项目，确保所有字段都是纯文本
 * @param item 行程项目
 * @returns 清洗后的行程项目
 */
function sanitizeItineraryItem(itItem: any): ItineraryItem | null {
  if (itItem === null || itItem === undefined) return null;

  if (typeof itItem === 'string') {
    // 可能是字符串化的对象 => 尝试解析，否则显示修剪后的内容
    try {
      const p = JSON.parse(itItem);
      if (p && typeof p === 'object') {
        return {
          id: p.id || undefined,
          time: p.time || '',
          name: stripCodeBlocks(p.name || p.title || ''),
          note: stripCodeBlocks(p.note || p.description || ''),
        };
      }
    } catch (e) {
      return { id: undefined, time: '', name: stripCodeBlocks(itItem), note: '' };
    }
  }

  if (typeof itItem === 'object') {
    const safeName = stripCodeBlocks(itItem.name || itItem.title || '');
    let safeNote = stripCodeBlocks(
      itItem.note || itItem.description || itItem.details || ''
    );

    // 如果note太长，截断
    if (safeNote.length > 500) {
      safeNote = safeNote.slice(0, 500) + '...';
    }

    return {
      id: itItem.id || undefined,
      time: itItem.time || '',
      name: safeName,
      note: safeNote,
    };
  }

  return null;
}

/**
 * 清洗生成的行程，移除多余的代码块和对象引用
 * @param itinerary 原始行程
 * @returns 清洗后的行程
 */
export function sanitizeGeneratedItinerary(
  itinerary: GeneratedItinerary | null
): GeneratedItinerary | null {
  if (!itinerary) return null;

  try {
    const deepClone = JSON.parse(JSON.stringify(itinerary));

    if (Array.isArray(deepClone.days)) {
      deepClone.days = deepClone.days.map((d: any) => {
        const day = { ...d };
        if (day.items && Array.isArray(day.items)) {
          day.items = day.items
            .map((item: any) => sanitizeItineraryItem(item))
            .filter((item: any) => item !== null);
        }
        return day;
      });
    }

    if (Array.isArray(deepClone.itinerary)) {
      deepClone.itinerary = deepClone.itinerary.map((d: any) => {
        const day = { ...d };
        if (day.items && Array.isArray(day.items)) {
          day.items = day.items
            .map((item: any) => sanitizeItineraryItem(item))
            .filter((item: any) => item !== null);
        }
        return day;
      });
    }

    return deepClone;
  } catch (e) {
    console.error('Error sanitizing itinerary:', e);
    return null;
  }
}

/**
 * 尝试从文本中解析JSON行程
 * @param text 文本内容
 * @returns 解析后的行程或null
 */
export function parseItineraryFromText(text: string): GeneratedItinerary | null {
  if (!text) return null;

  let cleanText = text.trim();

  // 移除代码块标记
  if (cleanText.startsWith('```')) {
    const match = cleanText.match(/```(?:json)?\s*([\s\S]*?)```/i);
    if (match && match[1]) {
      cleanText = match[1].trim();
    }
  }

  try {
    const parsed = JSON.parse(cleanText);
    if (
      parsed &&
      (Array.isArray(parsed.days) ||
        Array.isArray(parsed.itinerary) ||
        parsed.title)
    ) {
      return parsed;
    }
  } catch (e) {
    // 不是JSON，忽略
  }

  return null;
}

/**
 * 格式化日期为 YYYY-MM-DD
 * @param date 日期对象
 * @returns 格式化后的日期字符串
 */
export function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * 计算旅行天数
 * @param startDate 开始日期
 * @param endDate 结束日期
 * @returns 天数
 */
export function calculateTravelDays(startDate: Date, endDate: Date): number {
  const diffTime = endDate.getTime() - startDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays + 1; // 包含开始和结束日期
}

/**
 * 获取默认的表单数据
 * @returns 默认表单数据
 */
export function getDefaultFormData(): CreateTravelFormData {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return {
    projectName: '',
    projectDescription: '',
    startDate: today,
    endDate: tomorrow,
    minParticipants: String(DEFAULT_MIN_PARTICIPANTS),
    maxParticipants: String(DEFAULT_MAX_PARTICIPANTS),
    visibility: 'friends_only',
    paymentMode: 'prepay_average',
    budgetPerPerson: '',
    selectedPreferences: [],
    selectedCountry: '',
    selectedCity: '',
    selectedCurrency: 'CNY',
    selectedBackground: null,
  };
}
